import { clientEnv } from "@/lib/env";

export const CLERK_ACCOUNTS_ORIGIN = "https://accounts.manticscore.com";

export type ClerkRuntime = "embedded" | "primary" | "handoff";

export function isLocalHostname(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/** Vercel preview hosts are not Clerk satellites. Live pk_live_ → origin_invalid. */
export function isPreviewHostname(hostname: string): boolean {
  return hostname.endsWith(".vercel.app") || hostname.endsWith(".vercel.sh");
}

/**
 * Kept as the union of the two non-production host shapes, but deliberately
 * NOT used by `clerkRuntimeFor` any more: the preview case and the localhost
 * case sit on opposite sides of the `pk_test_` check now, and collapsing them
 * back into one call is what let a test key embed on a preview host.
 */
export function isNonProductionHostname(hostname: string): boolean {
  return isLocalHostname(hostname) || isPreviewHostname(hostname);
}

/**
 * One Clerk instance, everywhere: the live one.
 *
 * Localhost used to prefer a `pk_test_` stage key so Clerk's in-page embed was
 * allowed. That put local dev on a *different Clerk instance* from the API it
 * was calling — VITE_API_URL is production and `api/auth.py` pins a single
 * clerk_issuer and JWKS, so a stage token failed jwt.decode and every authed
 * call came back 401. Signing in locally looked like it worked and then
 * nothing loaded.
 *
 * With only the live key, localhost falls to `handoff` (see clerkRuntimeFor):
 * no Clerk mounted, sign-in goes out to accounts.manticscore.com and returns a
 * token the production API actually accepts.
 *
 * The stage key was read on localhost and nowhere else, so dropping the
 * preference retires it entirely.
 */
export function selectClerkPublishableKey(input: { live: string }): string {
  return input.live;
}

export function clerkRuntimeFor(key: string, hostname: string): ClerkRuntime {
  if (!key) return "handoff";
  // Preview hosts are checked BEFORE the key, and the order is the whole
  // point. A *.vercel.app / *.vercel.sh origin is not registered on any Clerk
  // instance, so mounting Clerk there fails with origin_invalid (#106)
  // regardless of which key is in play. Checking `pk_test_` first — as this
  // did — let a preview deploy configured with a test key embed anyway and
  // reproduce the exact failure this function exists to prevent. The
  // accompanying test claimed "whatever the key" while only ever passing it a
  // live one, so nothing caught it.
  if (isPreviewHostname(hostname)) return "handoff";
  // Kept as a property of the KEY, not of an environment: if someone
  // deliberately points the live var at a development instance, embedding is
  // still the right behaviour for it on a host Clerk will accept — which now
  // means localhost, a registered development origin. Nothing selects a test
  // key on its own any more.
  if (key.startsWith("pk_test_")) return "embedded";
  if (isLocalHostname(hostname)) return "handoff";
  // The production host is Clerk's PRIMARY domain, not a satellite of it.
  // `GET https://api.clerk.com/v1/domains` reports exactly one domain —
  // manticscore.com, `is_satellite: false`, with accounts.manticscore.com as
  // its hosted account portal. Mounting it as a satellite made Clerk require a
  // link domain that cannot exist, so `/v1/client/sync` answered 422
  // "link_domain must be included" and every signed-out visitor to /me landed
  // on a JSON error instead of a sign-in page.
  return "primary";
}

function currentHostname(): string {
  if (typeof window === "undefined") return "";
  return window.location.hostname;
}

export function clerkPublishableKey(): string {
  return selectClerkPublishableKey({ live: clientEnv.VITE_CLERK_PUBLISHABLE_KEY });
}

export function clerkRuntime(): ClerkRuntime {
  return clerkRuntimeFor(clerkPublishableKey(), currentHostname());
}

export function clerkSignInUrl(returnTo: string): string {
  return `${CLERK_ACCOUNTS_ORIGIN}/sign-in?redirect_url=${encodeURIComponent(returnTo)}`;
}

export function clerkSignUpUrl(returnTo: string): string {
  return `${CLERK_ACCOUNTS_ORIGIN}/sign-up?redirect_url=${encodeURIComponent(returnTo)}`;
}

export const clerkAppearance = {
  variables: {
    colorPrimary: "#2E73B8",
    colorBackground: "#1a1a1e",
    colorText: "#f4f4f5",
    colorTextSecondary: "#9a9aa3",
    colorInputBackground: "#242428",
    colorInputText: "#f4f4f5",
    colorNeutral: "#9a9aa3",
    borderRadius: "0.9rem",
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
  },
  elements: {
    rootBox: "mx-auto w-full",
    card: "bg-[#1a1a1e] shadow-none",
    headerTitle: "text-[#f4f4f5]",
    headerSubtitle: "text-[#9a9aa3]",
    socialButtonsBlockButton: "bg-[#242428] border-white/[0.09] text-[#f4f4f5]",
    formButtonPrimary: "bg-[#2E73B8] hover:bg-[#2E73B8]/90",
    footerActionLink: "text-[#2E73B8]",
  },
};
